import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/auth";
import { CIDADES_ATENDIDAS } from "@/content/site";
import ImageUploader from "@/admin/ImageUploader";

const TIPOS = ["Casa", "Apartamento", "Sobrado", "Terreno", "Casa de Praia"];
const inputCls = "h-11 rounded-none border-white/10 bg-white/5 text-sm text-stone-100 placeholder:text-stone-500 focus-visible:ring-0 focus-visible:border-brand/60";
const selectCls = "h-11 rounded-none border-white/10 bg-white/5 text-sm text-stone-100 focus:ring-0";
const contentCls = "rounded-none border-white/10 bg-elevated text-stone-200";

const EMPTY = {
  titulo: "", tipo: "Casa", negocio: "Venda", cidade: "Araricá", bairro: "", preco: "", area: "",
  quartos: "0", suites: "0", banheiros: "0", vagas: "0", destaque: false, descricao: "", caracteristicas: "", imagens: [],
};

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const AdminImovelForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => set(k)(e.target.value);

  useEffect(() => {
    if (!id) return;
    api.get("/admin/properties").then((r) => {
      const p = r.data.find((x) => x.id === id);
      if (!p) { toast.error("Imóvel não encontrado"); navigate("/admin/imoveis"); return; }
      setForm({
        ...p, preco: String(p.preco), area: String(p.area), quartos: String(p.quartos ?? 0), suites: String(p.suites ?? 0),
        banheiros: String(p.banheiros ?? 0), vagas: String(p.vagas ?? 0), caracteristicas: (p.caracteristicas || []).join("\n"), imagens: p.imagens || [],
      });
    }).finally(() => setLoading(false));
  }, [id, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.imagens.length) { toast.error("Adicione pelo menos uma foto."); return; }
    setSaving(true);
    const payload = {
      titulo: form.titulo, tipo: form.tipo, negocio: form.negocio, cidade: form.cidade, bairro: form.bairro,
      preco: Number(form.preco) || 0, area: Number(form.area) || 0, quartos: Number(form.quartos) || 0, suites: Number(form.suites) || 0,
      banheiros: Number(form.banheiros) || 0, vagas: Number(form.vagas) || 0, destaque: form.destaque, descricao: form.descricao,
      caracteristicas: form.caracteristicas.split("\n").map((s) => s.trim()).filter(Boolean), imagens: form.imagens,
    };
    try {
      if (id) await api.put(`/admin/properties/${id}`, payload);
      else await api.post("/admin/properties", payload);
      toast.success(id ? "Imóvel atualizado." : "Imóvel cadastrado.");
      navigate("/admin/imoveis");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-stone-500">Carregando...</p>;

  return (
    <div data-testid="admin-property-form-page">
      <Link to="/admin/imoveis" data-testid="admin-form-back" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400 hover:text-brand">
        <ArrowLeft size={13} /> Imóveis
      </Link>
      <h1 className="mt-4 font-serif text-2xl text-[#F4F4F6] sm:text-3xl">{id ? "Editar imóvel" : "Novo imóvel"}</h1>

      <form onSubmit={submit} className="mt-8 grid gap-10 lg:grid-cols-12" data-testid="admin-property-form">
        <div className="space-y-6 lg:col-span-7">
          <Field label="Título" testid="field-titulo">
            <Input required value={form.titulo} onChange={setE("titulo")} placeholder="Ex.: Casa 3 quartos no Centro" className={inputCls} data-testid="input-titulo" />
          </Field>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Negócio">
              <Select value={form.negocio} onValueChange={set("negocio")}>
                <SelectTrigger className={selectCls} data-testid="select-negocio"><SelectValue /></SelectTrigger>
                <SelectContent className={contentCls}>
                  <SelectItem value="Venda">Venda</SelectItem>
                  <SelectItem value="Aluguel">Aluguel</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tipo">
              <Select value={form.tipo} onValueChange={set("tipo")}>
                <SelectTrigger className={selectCls} data-testid="select-tipo"><SelectValue /></SelectTrigger>
                <SelectContent className={contentCls}>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Cidade">
              <Select value={form.cidade} onValueChange={set("cidade")}>
                <SelectTrigger className={selectCls} data-testid="select-cidade"><SelectValue /></SelectTrigger>
                <SelectContent className={contentCls}>{CIDADES_ATENDIDAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Bairro">
              <Input value={form.bairro} onChange={setE("bairro")} placeholder="Centro" className={inputCls} data-testid="input-bairro" />
            </Field>
            <Field label={form.negocio === "Aluguel" ? "Valor mensal (R$)" : "Preço (R$)"}>
              <Input required type="number" min="0" value={form.preco} onChange={setE("preco")} placeholder="250000" className={inputCls} data-testid="input-preco" />
            </Field>
            <Field label="Área (m²)">
              <Input required type="number" min="0" value={form.area} onChange={setE("area")} placeholder="90" className={inputCls} data-testid="input-area" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            {[["quartos", "Quartos"], ["suites", "Suítes"], ["banheiros", "Banheiros"], ["vagas", "Vagas"]].map(([k, label]) => (
              <Field key={k} label={label}>
                <Input type="number" min="0" value={form[k]} onChange={setE(k)} className={inputCls} data-testid={`input-${k}`} />
              </Field>
            ))}
          </div>
          <Field label="Descrição">
            <Textarea rows={6} value={form.descricao} onChange={setE("descricao")} placeholder="Descreva o imóvel, a localização e os diferenciais..." className="rounded-none border-white/10 bg-white/5 text-sm text-stone-100 placeholder:text-stone-500 focus-visible:ring-0 focus-visible:border-brand/60" data-testid="input-descricao" />
          </Field>
          <Field label="Características (uma por linha)">
            <Textarea rows={5} value={form.caracteristicas} onChange={setE("caracteristicas")} placeholder={"Aceita financiamento\nChurrasqueira\nGaragem coberta"} className="rounded-none border-white/10 bg-white/5 text-sm text-stone-100 placeholder:text-stone-500 focus-visible:ring-0 focus-visible:border-brand/60" data-testid="input-caracteristicas" />
          </Field>
          <div className="flex items-center justify-between border border-white/10 bg-coal px-5 py-4">
            <div>
              <p className="text-sm text-stone-100">Imóvel em destaque</p>
              <p className="text-xs text-stone-500">Aparece na página inicial (até 4 imóveis).</p>
            </div>
            <Switch checked={form.destaque} onCheckedChange={set("destaque")} data-testid="switch-destaque" className="data-[state=checked]:bg-brand" />
          </div>
        </div>

        <div className="lg:col-span-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Fotos</p>
          <div className="mt-2">
            <ImageUploader images={form.imagens} onChange={set("imagens")} />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-property-submit"
            className="mt-10 inline-flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-brand-light disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Salvando..." : id ? "Salvar alterações" : "Cadastrar imóvel"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminImovelForm;
